import {
    DownOutlined, RightOutlined,
} from '@ant-design/icons';
import {
    ConfigProvider, Flex, Image, Popover, Space, TableColumnType, TableProps, Tag, theme as AntdTheme, Typography,
} from 'antd';
import React, { useMemo } from 'react';

import {
    ImagePlaceholder, ScriptActiveBadge, StatusIcon,
} from '@/components';
import {
    ColumnKey, ColumnTitle,
    DARK_THEME_HTML_BACKGROUND_COLOR,
    UserRole,
    UserRoles,
} from '@/constants';
import { useUIStore } from '@/stores';
import { lightTheme } from '@/themes';
import {
    AbstractEntity,
    Event,
    Script,
    SupportTicket,
    User,
} from '@/types';
import { formatDate } from '@/utils';

export const getTitleColumn = <T extends AbstractEntity>(onClick?: (record: T) => void): TableColumnType<T> => {
    return {
        title: ColumnTitle[ColumnKey.Title],
        dataIndex: ColumnKey.Title,
        key: ColumnKey.Title,
        width: 300,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (title: string, record: T) => {
            if (onClick) {
                return (
                    <Typography.Link
                        onClick={() => onClick(record)}
                        style={{ cursor: 'pointer' }}
                    >
                        {title}
                    </Typography.Link>
                );
            }
            return (
                <span>
                    {title}
                </span>
            );
        },
    };
};

export const getCreatedAtColumn = <T extends AbstractEntity | User | Event | SupportTicket>(): TableColumnType<T> => {
    return {
        title: ColumnTitle[ColumnKey.CreatedAt],
        dataIndex: ColumnKey.CreatedAt,
        key: ColumnKey.CreatedAt,
        width: 160,
        ellipsis: true,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (date: string) => formatDate(date),
    };
};

export const getUpdatedAtColumn = <T extends AbstractEntity | User>(): TableColumnType<T> => {
    return {
        title: ColumnTitle[ColumnKey.UpdatedAt],
        dataIndex: ColumnKey.UpdatedAt,
        key: ColumnKey.UpdatedAt,
        width: 160,
        ellipsis: true,
        sorter: true,
        align: 'center',
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (date: string) => formatDate(date),
    };
};

export const getDescriptionColumn = <T extends AbstractEntity | Event>(): TableColumnType<T> => {
    return {
        title: ColumnTitle[ColumnKey.Description],
        dataIndex: ColumnKey.Description,
        key: ColumnKey.Description,
        width: 350,
        render: (description: string | null) => description || '-',
        ellipsis: true,
    };
};

export const getEventDescriptionColumn = (onClick?: (record: Event) => void): TableColumnType<Event> => {
    return {
        title: ColumnTitle[ColumnKey.EventDescription],
        dataIndex: ColumnKey.Description,
        key: ColumnKey.Description,
        width: 350,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (description: string, record: Event) => {
            if (onClick) {
                return (
                    <Typography.Link
                        onClick={() => onClick(record)}
                        style={{ cursor: 'pointer' }}
                    >
                        {description}
                    </Typography.Link>
                );
            }
            return description;
        },
    };
};

export const getEventUserColumn = (): TableColumnType<Event> => {
    return {
        title: ColumnTitle[ColumnKey.User],
        dataIndex: ColumnKey.User,
        key: ColumnKey.User,
        width: 200,
        render: (user: Event['user']) => {
            if (!user) {
                return '-';
            }

            const displayText = user.name || user.nickname || user.email || '-';

            const popoverContent = (
                <div>
                    <div><strong>Нікнейм:</strong> {user.nickname || '-'}</div>
                    <div><strong>Email:</strong> {user.email || '-'}</div>
                    <div><strong>Коментар:</strong> {user.comment || '-'}</div>
                </div>
            );

            return (
                <Popover content={popoverContent} title="Інформація про користувача">
                    <span style={{ cursor: 'pointer' }}>{displayText}</span>
                </Popover>
            );
        },
    };
};

export const getEventPropertiesColumn = (): TableColumnType<Event> => {
    const getValueLabel = (value: unknown): string => {
        if (value === undefined) return '—';
        if (value === null) return 'null';
        if (Array.isArray(value)) return `масив (${value.length})`;
        if (typeof value === 'object') return 'обʼєкт';
        if (typeof value === 'string') return value.length > 28 ? `${value.slice(0, 28)}...` : value;
        return String(value);
    };

    return {
        title: ColumnTitle[ColumnKey.Properties],
        dataIndex: ColumnKey.Properties,
        key: ColumnKey.Properties,
        width: 200,
        ellipsis: true,
        render: (properties: Event['properties']) => {
            if (!properties || Object.keys(properties).length === 0) {
                return '—';
            }

            const hasOld = Object.prototype.hasOwnProperty.call(properties, 'old');
            const hasNew = Object.prototype.hasOwnProperty.call(properties, 'new');
            const extraKeys = Object.keys(properties).filter((key) => key !== 'old' && key !== 'new');

            return (
                <Flex vertical gap={4}>
                    {hasOld && (
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            було: {getValueLabel(properties.old)}
                        </Typography.Text>
                    )}
                    {hasNew && (
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            стало: {getValueLabel(properties.new)}
                        </Typography.Text>
                    )}
                    {extraKeys.length > 0 && (
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            додатково: {extraKeys.join(', ')}
                        </Typography.Text>
                    )}
                </Flex>
            );
        },
    };
};

export const getEventDetailsColumn = ({
    render,
}: {
    render: TableColumnType<Event>['render'];
}): TableColumnType<Event> => {
    return {
        title: ColumnTitle[ColumnKey.Details],
        dataIndex: ColumnKey.Details,
        key: ColumnKey.Details,
        width: 200,
        render,
    };
};

export const getActionsColumn = <T extends AbstractEntity | User | Script | SupportTicket>({
    render,
    width = 120,
}: {
    render: TableColumnType<T>['render'];
    width?: number;
}): TableColumnType<T> => {
    return {
        title: ColumnTitle[ColumnKey.Actions],
        key: ColumnKey.Actions,
        width,
        fixed: 'right',
        align: 'center',
        render,
    };
};

export const getNicknameColumn = (onClick?: (record: User) => void): TableColumnType<User> => {
    return {
        title: ColumnTitle[ColumnKey.NickName],
        dataIndex: ColumnKey.NickName,
        key: ColumnKey.NickName,
        width: 250,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (nickname: string, record: User) => {
            if (onClick) {
                return (
                    <Typography.Link
                        onClick={() => onClick(record)}
                        style={{ cursor: 'pointer' }}
                    >
                        {nickname}
                    </Typography.Link>
                );
            }
            return (
                <span>
                    {nickname}
                </span>
            );
        },
    };
};

export const getNameColumn = (): TableColumnType<User> => {
    return {
        title: ColumnTitle[ColumnKey.Name],
        dataIndex: ColumnKey.Name,
        key: ColumnKey.Name,
        width: 220,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (name: string | null) => name || '—',
    };
};

export const getEmailColumn = (): TableColumnType<User> => {
    return {
        title: ColumnTitle[ColumnKey.Email],
        dataIndex: ColumnKey.Email,
        key: ColumnKey.Email,
        width: 250,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (email: string) => email,
    };
};

export const getIsActiveColumn = (): TableColumnType<User> => {
    return {
        title: ColumnTitle[ColumnKey.IsActive],
        dataIndex: ColumnKey.IsActive,
        key: ColumnKey.IsActive,
        width: 150,
        align: 'center',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (is_active: number) => <StatusIcon isActive={Boolean(is_active)} />,
    };
};

export const getRoleColumn = (): TableColumnType<User> => {
    return {
        title: ColumnTitle[ColumnKey.Role],
        dataIndex: ColumnKey.Role,
        key: ColumnKey.Role,
        width: 150,
        render: (role: UserRole) => UserRoles[role] || 'Не визначено',
    };
};

export const getExpandableConfig = <T extends { description?: string | null; body?: string | null; deleted?: boolean }>({
    getContent,
    emptyText = 'Опис відсутній',
}: {
    getContent: (record: T) => string | null;
    emptyText?: string;
}) => {
    return {
        expandIcon: ({ expanded, onExpand, record }: any) => (
            expanded ? (
                <DownOutlined onClick={(evt) => onExpand(record, evt)} />
            ) : (
                <RightOutlined onClick={(evt) => onExpand(record, evt)} />
            )
        ),
        expandedRowRender: (record: T) => {
            const content = getContent(record);

            return content ? (
                <>
                    <span>
                        Тези:
                    </span>
                    <br />
                    {content}
                </>
            ) : emptyText;
        },
    };
};

export const getProjectsAndScriptsCountersColumn = (render: TableColumnType<User>['render']): TableColumnType<User> => {
    return {
        title: ColumnTitle[ColumnKey.ProjectsAndScriptsCounter],
        dataIndex: ColumnKey.ProjectsAndScriptsCounter,
        key: ColumnKey.ProjectsAndScriptsCounter,
        width: 180,
        align: 'center',
        render,
    };
};

type ScriptColumnProps = {
    title?: string;
    width?: number;
    onClick?: (record: Script) => void;
    onPreviewOpen?: (record: Script) => void;
};

export const getScriptTitleColumn = ({
    title = 'Назва скрипта',
    width = 300,
    onClick,
}: ScriptColumnProps = {}): TableColumnType<Script> => {
    return {
        title,
        dataIndex: ColumnKey.Title,
        key: ColumnKey.Title,
        width,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (title: string, record: Script) => {
            const isActive = Boolean(record.is_active ?? true);
            const titleNode = onClick ? (
                <Typography.Link
                    onClick={() => onClick(record)}
                    style={{ cursor: 'pointer' }}
                >
                    {title}
                </Typography.Link>
            ) : (
                <span>{title}</span>
            );
            return (
                <Space size="small" align="center" wrap>
                    {titleNode}
                    <ScriptActiveBadge isActive={isActive} />
                </Space>
            );
        },
    };
};

export const getScriptPriceColumn = ({
    title = 'Ціна',
    width = 120,
}: ScriptColumnProps = {}): TableColumnType<Script> => {
    return {
        title,
        dataIndex: 'product_price',
        key: 'product_price',
        width,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (price: string | null) => price || '-',
    };
};

export const getScriptSkuColumn = ({
    title = 'Артикул',
    width = 150,
}: ScriptColumnProps = {}): TableColumnType<Script> => {
    return {
        title,
        dataIndex: 'product_sku',
        key: 'product_sku',
        width,
        sorter: (a, b) => (a.product_sku || '').localeCompare(b.product_sku || '', 'uk-UA'),
        sortDirections: ['ascend', 'descend', 'ascend'],
    };
};

export const getScriptProductIdColumn = ({
    title = 'ID',
    width = 80,
}: ScriptColumnProps = {}): TableColumnType<Script> => {
    return {
        title,
        dataIndex: 'product_id',
        key: 'product_id',
        width,
        sorter: false,
        align: 'center',
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (content) => {
            return (
                <Typography.Text
                    ellipsis={{ tooltip: content }}
                >
                    {content}
                </Typography.Text>
            );
        },
    };
};

export const getScriptImageColumn = ({
    width = 80,
    onPreviewOpen,
}: ScriptColumnProps = {}): TableColumnType<Script> => {
    return {
        dataIndex: 'image',
        key: 'image',
        width,
        render: (_: unknown, record: Script) => (
            record.main_image?.thumb ? (
                <Image
                    width={50}
                    height={50}
                    src={record.main_image.thumb}
                    alt={record.title}
                    style={{
                        objectFit: 'cover',
                        borderRadius: 4,
                        cursor: onPreviewOpen ? 'pointer' : 'zoom-in',
                    }}
                    onClick={onPreviewOpen ? () => onPreviewOpen(record) : undefined}
                    preview={onPreviewOpen ? false : {
                        src: record.main_image.url,
                    }}
                />
            ) : (
                <ImagePlaceholder width={50} height={50} iconSize={25} />
            )
        ),
    };
};

const ExpandedRowContent = ({ description }: { description: string }) => {
    const { theme } = useUIStore();

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);

    return (
        <ConfigProvider
            theme={{
                algorithm: theme === 'dark' ? AntdTheme.defaultAlgorithm : undefined,
                token: theme === 'dark' ? {
                    colorText: lightThemeToken.colorText,
                    colorLink: lightThemeToken.colorLink,
                } : undefined,
            }}
        >
            <div
                dangerouslySetInnerHTML={{ __html: description }}
                style={{
                    padding: '8px',
                    backgroundColor: theme === 'dark' ? DARK_THEME_HTML_BACKGROUND_COLOR : 'transparent',
                    color: theme === 'dark' ? lightThemeToken.colorText : undefined,
                }}
            />
        </ConfigProvider>
    );
};

export const getScriptExpandableConfig = (): TableProps<Script>['expandable'] => {
    return {
        expandedRowRender: (record: Script) => {
            const description = record.description || 'Опис відсутній';
            return <ExpandedRowContent description={description} />;
        },
        rowExpandable: (record: Script) => !!record.description,
        expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
                <DownOutlined
                    onClick={(e) => onExpand(record, e)}
                    style={{ cursor: 'pointer', marginRight: 8 }}
                />
            ) : (
                <RightOutlined
                    onClick={(e) => onExpand(record, e)}
                    style={{ cursor: 'pointer', marginRight: 8 }}
                />
            ),
    };
};

export const getSupportTicketNumberColumn = (): TableColumnType<SupportTicket> => {
    return {
        title: 'Номер',
        dataIndex: 'ticket_number',
        key: 'ticket_number',
        width: 120,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (ticket_number: number) => `#${ticket_number}`,
    };
};

export const getSupportTicketSubjectColumn = (onClick?: (record: SupportTicket) => void): TableColumnType<SupportTicket> => {
    return {
        title: 'Тема',
        dataIndex: 'subject',
        key: 'subject',
        width: 250,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        ellipsis: true,
        render: (subject: string, record: SupportTicket) => {
            if (onClick) {
                return (
                    <Typography.Link
                        onClick={() => onClick(record)}
                        style={{ cursor: 'pointer' }}
                    >
                        {subject}
                    </Typography.Link>
                );
            }
            return subject;
        },
    };
};

export const getSupportTicketDescriptionColumn = (): TableColumnType<SupportTicket> => {
    return {
        title: 'Опис',
        dataIndex: 'description',
        key: 'description',
        width: 300,
        ellipsis: true,
        render: (description: string | null) => description || '-',
    };
};

export const getSupportTicketTypeColumn = ({
    render,
}: {
    render?: TableColumnType<SupportTicket>['render'];
} = {}): TableColumnType<SupportTicket> => {
    return {
        title: 'Тип',
        dataIndex: 'type',
        key: 'type',
        width: 150,
        render: render || ((type: string) => type),
    };
};

export const getSupportTicketStatusColumn = ({
    render,
}: {
    render?: TableColumnType<SupportTicket>['render'];
} = {}): TableColumnType<SupportTicket> => {
    return {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: render || ((status: string) => <Tag>{status}</Tag>),
    };
};

export const getSupportTicketPriorityColumn = ({
    render,
}: {
    render?: TableColumnType<SupportTicket>['render'];
} = {}): TableColumnType<SupportTicket> => {
    return {
        title: 'Пріоритет',
        dataIndex: 'priority',
        key: 'priority',
        width: 150,
        render: render || ((priority: string | null) => priority || '-'),
    };
};

export const getSupportTicketDetailsColumn = ({
    render,
}: {
    render: TableColumnType<SupportTicket>['render'];
}): TableColumnType<SupportTicket> => {
    return {
        title: 'Деталі',
        dataIndex: 'details',
        key: 'details',
        width: 100,
        align: 'center',
        render,
    };
};
